import { useCheckAccountTraits } from '@cmg/auth';
import { UUID } from '@cmg/common';
import { FC } from 'react';

import { getOfferingTypeLabel } from '../../../../common/util/offering/offeringTypeHelpers';
import {
  OfferingStatus as OfferingStatusEnum,
  OfferingType,
  Sector,
  SecurityType,
} from '../../../../graphql';
import { Offering_OfferingHeader_OfferingTermsPartsFragment } from '../../graphql';
import { ConvertibleOfferingHighlights } from './ConvertibleOfferingHighlights';
import { OfferingHighlights } from './OfferingHighlights';
import { PrivatePlacementOfferingHighlights } from './PrivatePlacementOfferingHighlights';

export type Props = {
  offeringId?: UUID;
  issuerName: string;
  type?: OfferingType;
  status?: OfferingStatusEnum;
  sector?: Sector | null;
  sizeInShares?: number | null;
  securityType?: SecurityType;
  terms?: Offering_OfferingHeader_OfferingTermsPartsFragment;
  pricingCountryCode?: string | null;
  pricingCurrencyCode?: string;
};

/**
 * OfferingDomainHighlights picks the highlights variant matching the offering type.
 */
export const OfferingDomainHighlights: FC<Props> = ({
  offeringId,
  issuerName,
  type,
  status,
  sector,
  sizeInShares,
  securityType,
  terms,
  pricingCountryCode,
  pricingCurrencyCode,
}) => {
  const hasCreateInternationalTrait = useCheckAccountTraits(['XC_CREATE_INTERNATIONAL']);
  const isCreateNewOffering = !offeringId;
  const offeringTypeLabel =
    (hasCreateInternationalTrait && !pricingCountryCode) || !type
      ? '-'
      : getOfferingTypeLabel(type);

  if (type === OfferingType.Convertible) {
    return (
      <ConvertibleOfferingHighlights
        issuerName={issuerName}
        type={type}
        hasCreateInternationalTrait={hasCreateInternationalTrait}
        isCreateNewOffering={isCreateNewOffering}
        pricingCountryCode={pricingCountryCode}
        sector={sector}
        terms={terms}
        pricingCurrencyCode={pricingCurrencyCode}
        status={status}
        offeringTypeLabel={offeringTypeLabel}
      />
    );
  }

  if (type === OfferingType.PrivatePlacement) {
    return (
      <PrivatePlacementOfferingHighlights
        issuerName={issuerName}
        isCreateNewOffering={isCreateNewOffering}
        sector={sector}
        sizeShares={terms?.privatePlacementTerms?.sizeShares}
        status={status}
      />
    );
  }

  return (
    <OfferingHighlights
      issuerName={issuerName}
      type={type}
      isCreateNewOffering={isCreateNewOffering}
      sector={sector}
      securityType={securityType}
      sizeInShares={sizeInShares}
      terms={terms}
      pricingCurrencyCode={pricingCurrencyCode}
      status={status}
      offeringTypeLabel={offeringTypeLabel}
    />
  );
};
